// src/components/customerModule/customers/ExportButtons.jsx
//
// Two controls, one row:
//   Export CSV    → the tab that's open right now (via CsvExportContext)
//   Export Excel  → every tab, one sheet each (via customerExportConfig)

import React, { useState } from "react";
import { Box, Tooltip, CircularProgress, Snackbar, Alert } from "@mui/material";
import { useCsvExportTarget } from "./CsvExportContext.jsx";
import { exportRowsToCsv, dateStamp, safeFileSegment } from "./csvExport.js";
import { exportCustomerWorkbook } from "./customerWorkbook.js";

const BLUE = "#378ADD";

const pillSx = (disabled, filled) => ({
    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
    userSelect: "none", whiteSpace: "nowrap",
    border: "0.5px solid",
    borderColor: disabled ? "divider" : BLUE,
    background: filled && !disabled ? BLUE : "transparent",
    color: disabled ? "text.disabled" : filled ? "#fff" : BLUE,
    fontWeight: 500,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.5 : 1,
    display: "inline-flex", alignItems: "center", gap: "6px",
    transition: "all .15s",
    "&:hover": disabled ? {} : { background: filled ? "#2a6db5" : "rgba(55,138,221,0.06)" },
});

const ExportButtons = ({ customerguid, profile }) => {
    const target = useCsvExportTarget();
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState("");
    const [toast, setToast] = useState(null);

    const csvRows = target?.rows || [];
    const csvDisabled = !target || !target.columns?.length || csvRows.length === 0 || busy;
    const excelDisabled = !customerguid || busy;

    const handleCsv = () => {
        if (csvDisabled) return;
        const filename =
            [safeFileSegment(target.filePrefix), safeFileSegment(target.name), dateStamp()]
                .filter(Boolean)
                .join("_") + ".csv";
        exportRowsToCsv({ filename, columns: target.columns, rows: csvRows });
    };

    const handleExcel = async () => {
        if (excelDisabled) return;
        setBusy(true);
        setProgress("Starting");
        try {
            const results = await exportCustomerWorkbook({
                customerguid,
                profile,
                onProgress: ({ done, total, label }) => setProgress(`${label} (${done}/${total})`),
            });
            const failed = results.filter((r) => r.error).map((r) => r.section.sheet);
            setToast(
                failed.length
                    ? { severity: "warning", text: `Workbook downloaded. Missing data: ${failed.join(", ")}.` }
                    : { severity: "success", text: "Workbook downloaded." }
            );
        } catch (e) {
            console.error("Excel export failed", e);
            setToast({ severity: "error", text: "Export failed. Try again." });
        } finally {
            setBusy(false);
            setProgress("");
        }
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={csvDisabled ? "Nothing to export on this tab" : `Export ${csvRows.length.toLocaleString()} rows from this tab`}>
                    <span>
                        <Box onClick={csvDisabled ? undefined : handleCsv} sx={pillSx(csvDisabled, false)}>
                            <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                            Export CSV
                        </Box>
                    </span>
                </Tooltip>

                <Tooltip title={busy ? progress : "Export every tab to one Excel workbook"}>
                    <span>
                        <Box onClick={excelDisabled ? undefined : handleExcel} sx={pillSx(excelDisabled, true)}>
                            {busy
                                ? <CircularProgress size={13} sx={{ color: "inherit" }} />
                                : <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>}
                            {busy ? "Exporting…" : "Export Excel (All tabs)"}
                        </Box>
                    </span>
                </Tooltip>
            </Box>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {toast ? (
                    <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: "100%" }}>
                        {toast.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
};

export default ExportButtons;